import { Edit, Visibility } from '@mui/icons-material'
import { Box, IconButton } from '@mui/material'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'
import AddButton from '../../../components/Buttons/AddButton'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import { TableTitleTypography } from '../../../components/Typographies/TableTitleTypography'
import { IHsLocalTariff } from '../types'
import HsLocalTariffModal from './HsLocalTariffModal'
import { useLocalTariffManager } from './hooks/useLocalTariffManager'
import { formatClassificationCode } from './utils/hsCodeUtils'

interface HsLocalTariffTableProps {
  readonly isDisabled: boolean
}

const HsLocalTariffTable: React.FC<HsLocalTariffTableProps> = memo(({ isDisabled = false }) => {
  const { watch, setValue } = useFormContext()
  const { data: countries = [] } = useGetCountriesQuery(undefined, { skip: isDisabled })

  const hsLocalTariffs = watch('hsLocalTariffs') || []
  const hsSubheadingId = watch('hsSubheadingId')

  const { state, actions } = useLocalTariffManager(hsLocalTariffs)

  // Sync form data with local state
  useEffect(() => {
    actions.setTariffs(hsLocalTariffs)
  }, [hsLocalTariffs, actions])

  const columns = useMemo<MRTColumnDef<IHsLocalTariff>[]>(() => [
    {
      accessorKey: 'description',
      header: 'Description',
    },
    {
      accessorFn: (row) => formatClassificationCode(row.classificationCode),
      header: 'HS Local Classification Code (optional)',
    },
    {
      accessorFn: (row) =>
        countries.find((country: any) => country.id === row.countryId)?.name
        ?? row.country?.name
        ?? '',
      header: 'Country',
    },
    {
      accessorFn: (row) => `${row.tariff ?? 0} %`,
      header: 'Tariff (%)',
    },
  ], [countries])

  const handleCreateTariff = useCallback((hsLocalTariff: IHsLocalTariff) => {
    const updatedTariffs = [...state.tariffs, hsLocalTariff]
    setValue('hsLocalTariffs', updatedTariffs)
    actions.closeModal('create')
    actions.resetSelection()
  }, [state.tariffs, setValue, actions])

  const handleUpdateTariff = useCallback((hsLocalTariff: IHsLocalTariff) => {
    if (state.selectedIndex >= 0) {
      const updatedTariffs = [...state.tariffs]
      updatedTariffs[state.selectedIndex] = hsLocalTariff
      setValue('hsLocalTariffs', updatedTariffs)
      actions.closeModal('edit')
      actions.resetSelection()
    }
  }, [state.tariffs, state.selectedIndex, setValue, actions])

  const handleEditClick = useCallback((rowIndex: number) => {
    actions.setSelectedIndex(rowIndex)
    actions.openModal('edit')
  }, [actions])

  const handleViewClick = useCallback((rowIndex: number) => {
    actions.setSelectedIndex(rowIndex)
    actions.openModal('view')
  }, [actions])

  const handleCreateClick = useCallback(() => {
    actions.openModal('create')
  }, [actions])

  const handleCloseModal = useCallback((modalType: 'create' | 'edit' | 'view') => {
    actions.closeModal(modalType)
    actions.resetSelection()
  }, [actions])

  const renderRowActions = useCallback(({ row }: any) => (
    <Box sx={{ display: 'flex' }}>
      <IconButton
        color={'primary'}
        onClick={() => handleViewClick(row?.index)}
      >
        <Visibility />
      </IconButton>
      {!isDisabled && (
        <IconButton
          color={'primary'}
          onClick={() => handleEditClick(row?.index)}
        >
          <Edit />
        </IconButton>
      )}
    </Box>
  ), [isDisabled, handleEditClick, handleViewClick])

  return (
    <Box sx={{ pt: '32px', pb: '32px', width: '100%' }}>
      <HsLocalTariffModal
        isOpen={state.modalStates.create}
        handleCancel={() => handleCloseModal('create')}
        handleSubmit={handleCreateTariff}
      />

      <HsLocalTariffModal
        isOpen={state.modalStates.edit}
        handleCancel={() => handleCloseModal('edit')}
        hsLocalTariffToEdit={actions.getSelectedTariff()}
        handleSubmit={handleUpdateTariff}
      />

      <HsLocalTariffModal
        isOpen={state.modalStates.view}
        hsLocalTariffToEdit={actions.getSelectedTariff()}
        handleCancel={() => handleCloseModal('view')}
      />

      <MRTDataGridv2
        leftHeadingComponent={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TableTitleTypography sx={{ fontSize: '20px' }}>
              Country-specific import tariffs
            </TableTitleTypography>
          </Box>
        }
        rightHeadingComponent={
          <ConditionalRender condition={!isDisabled}>
            <AddButton
              disabled={!hsSubheadingId}
              text='New'
              onClick={handleCreateClick}
            />
          </ConditionalRender>
        }
        columns={columns}
        data={state.tariffs}
        getRowId={(row) => row.id as any}
        enableEditing
        enableRowActions={!isDisabled}
        positionActionsColumn='last'
        renderRowActions={renderRowActions}
      />
    </Box>
  )
})

HsLocalTariffTable.displayName = 'HsLocalTariffTable'

export default HsLocalTariffTable
